/**
 * 🔐 Stockage Local Sécurisé avec Chiffrement AES
 * 
 * SEC-003: Protection des données sensibles en localStorage
 * contre les attaques XSS potentielles.
 * 
 * Utilise SubtleCrypto (API Web Crypto native) pour le chiffrement AES-GCM.
 * Pas de dépendance externe requise.
 */

import { logger } from '@/utils/logger';

// Clé de chiffrement dérivée par utilisateur (mise en cache en mémoire)
const keyCache = new Map<string, CryptoKey>();

/**
 * Récupère ou génère un secret d'installation aléatoire (32 octets, hex).
 * Combiné avec userId dans PBKDF2 : connaître userId seul ne suffit pas
 * à dériver la clé depuis un dump localStorage volé.
 */
function getInstallSecret(): string {
  const KEY = '_app_sec_v1';
  const stored = localStorage.getItem(KEY);
  if (stored) return stored;
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
  localStorage.setItem(KEY, hex);
  return hex;
}

/**
 * Récupère ou génère un sel aléatoire par utilisateur.
 * Stocké en clair dans localStorage (le sel n'est pas secret,
 * il doit juste être unique et persistant).
 */
function getUserSalt(userId: string): Uint8Array {
  const saltKey = `secure_salt_${userId}`;
  const stored = localStorage.getItem(saltKey);
  if (stored) {
    return Uint8Array.from(atob(stored), c => c.charCodeAt(0));
  }
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const saltB64 = btoa(String.fromCharCode(...salt));
  localStorage.setItem(saltKey, saltB64);
  return salt;
}

/**
 * Dérive une clé de chiffrement AES-256 via PBKDF2.
 * Password = userId + ':' + installSecret — les deux doivent être connus
 * pour reconstruire la clé à partir d'un dump localStorage.
 */
async function deriveKey(userId: string): Promise<CryptoKey> {
  if (keyCache.has(userId)) {
    return keyCache.get(userId)!;
  }

  const encoder = new TextEncoder();
  const salt = getUserSalt(userId);
  const password = `${userId}:${getInstallSecret()}`;

  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey']
  );

  const key = await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: 100000,
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );

  keyCache.set(userId, key);
  return key;
}

/**
 * Convertit un Uint8Array en base64 de manière sûre (évite stack overflow)
 */
function uint8ArrayToBase64(bytes: Uint8Array): string {
  // Utiliser une approche par chunks pour éviter "Maximum call stack size exceeded"
  const CHUNK_SIZE = 0x8000; // 32KB chunks
  let result = '';
  for (let i = 0; i < bytes.length; i += CHUNK_SIZE) {
    const chunk = bytes.subarray(i, i + CHUNK_SIZE);
    result += String.fromCharCode(...chunk);
  }
  return btoa(result);
}

/**
 * Chiffre des données avec AES-GCM
 */
async function encrypt(data: string, key: CryptoKey): Promise<string> {
  const encoder = new TextEncoder();
  const iv = crypto.getRandomValues(new Uint8Array(12)); // IV de 96 bits pour AES-GCM
  
  const encryptedBuffer = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoder.encode(data)
  );

  // Combiner IV + données chiffrées en base64
  const combined = new Uint8Array(iv.length + encryptedBuffer.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(encryptedBuffer), iv.length);
  
  // Utiliser la fonction sûre pour éviter stack overflow avec de grandes données
  return uint8ArrayToBase64(combined);
}

/**
 * Déchiffre des données avec AES-GCM
 */
async function decrypt(encryptedData: string, key: CryptoKey): Promise<string> {
  try {
    const combined = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));
    
    // Extraire IV (12 premiers bytes) et données chiffrées
    const iv = combined.slice(0, 12);
    const data = combined.slice(12);

    const decryptedBuffer = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      data
    );

    return new TextDecoder().decode(decryptedBuffer);
  } catch (error) {
    logger.error('Erreur de déchiffrement:', error);
    throw new Error('DECRYPTION_FAILED');
  }
}

/**
 * Helper de stockage sécurisé
 * Chiffre automatiquement les données avant stockage
 */
export const secureStorage = {
  /**
   * Stocke des données chiffrées
   * @param key - Clé de stockage
   * @param value - Données à stocker (sera sérialisé en JSON)
   * @param userId - ID utilisateur pour la dérivation de clé
   */
  async setItem<T>(key: string, value: T, userId: string): Promise<void> {
    try {
      const cryptoKey = await deriveKey(userId);
      const jsonData = JSON.stringify(value);
      const encrypted = await encrypt(jsonData, cryptoKey);
      localStorage.setItem(`secure_${key}`, encrypted);
    } catch (error) {
      logger.error(`Erreur lors du stockage sécurisé de ${key}:`, error);
      throw error;
    }
  },

  /**
   * Récupère et déchiffre des données
   * @param key - Clé de stockage
   * @param userId - ID utilisateur pour la dérivation de clé
   * @returns Les données déchiffrées ou null si non trouvées
   */
  async getItem<T>(key: string, userId: string): Promise<T | null> {
    try {
      const encrypted = localStorage.getItem(`secure_${key}`);
      if (!encrypted) return null;

      const cryptoKey = await deriveKey(userId);
      const decrypted = await decrypt(encrypted, cryptoKey);
      return JSON.parse(decrypted) as T;
    } catch (error) {
      logger.error(`Erreur lors de la lecture sécurisée de ${key}:`, error);
      // Si déchiffrement échoue, supprimer les données corrompues
      localStorage.removeItem(`secure_${key}`);
      return null;
    }
  },

  /**
   * Supprime des données
   * @param key - Clé de stockage
   */
  removeItem(key: string): void {
    localStorage.removeItem(`secure_${key}`);
  },

  /**
   * Vérifie si une clé existe
   * @param key - Clé de stockage
   */
  hasItem(key: string): boolean {
    return localStorage.getItem(`secure_${key}`) !== null;
  },

  /**
   * Efface le cache de clés (à appeler lors de la déconnexion)
   */
  clearKeyCache(): void {
    keyCache.clear();
  },
  
  /**
   * Efface toutes les données sécurisées pour un utilisateur
   * @param userId - ID utilisateur (non utilisé mais gardé pour compatibilité)
   */
  async clear(userId: string): Promise<void> {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('secure_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    keyCache.clear();
  },

  /**
   * Migre les données non chiffrées vers le stockage sécurisé
   * @param key - Clé de stockage
   * @param userId - ID utilisateur
   */
  async migrateFromPlainStorage<T>(key: string, userId: string): Promise<T | null> {
    try {
      // Vérifier si données non chiffrées existent
      const plainData = localStorage.getItem(key);
      if (!plainData) return null;

      // Vérifier si déjà migré
      if (this.hasItem(key)) {
        // Supprimer l'ancienne version non chiffrée
        localStorage.removeItem(key);
        return await this.getItem(key, userId) as T | null;
      }

      // Parser et migrer
      const data = JSON.parse(plainData) as T;
      await this.setItem(key, data, userId);
      
      // Supprimer l'ancienne version non chiffrée
      localStorage.removeItem(key);
      
      return data;
    } catch (error) {
      logger.error(`Erreur lors de la migration de ${key}:`, error);
      return null;
    }
  }
};

// Alias pour compatibilité avec le code existant
export const secureLocalStorage = secureStorage;

/**
 * Vérifie si le stockage sécurisé est disponible
 * (SubtleCrypto doit être présent)
 */
export function isSecureStorageAvailable(): boolean {
  return typeof crypto !== 'undefined' && 
         typeof crypto.subtle !== 'undefined' &&
         typeof crypto.subtle.deriveKey === 'function';
}

/**
 * Hook pour migrer automatiquement les données sensibles
 * À appeler au démarrage de l'application pour les utilisateurs connectés
 */
export async function migrateToSecureStorage(userId: string): Promise<void> {
  const sensitiveKeys = [
    'pets',
    'vaccines', 
    'weightEntries',
    'medications',
    'appointments',
    'veterinarians',
    'dewormings',
    'gps-trackers'
  ];

  for (const key of sensitiveKeys) {
    await secureStorage.migrateFromPlainStorage(key, userId);
  }
}

export default secureStorage;
